import './browser';
import './polyfill';
import { YDeferred } from './types';

export type YSortSpec<Model> = [comparator: (lhs: Model, rhs: Model, o: 1 | -1) => number, order: 1 | -1];


///////////////////
///// UTILITY /////
///////////////////

export function yResolve<T extends Exclude<any, Function>, U extends any[]>(p: T | ((...u: U) => T), ...args: U): T {
    return typeof p == 'function' ? (p as ((...u: U) => T))(...args) : p;
}

export function yResolveThen<T extends Exclude<Exclude<any, Function>, Promise<any>>, U>(arg: T | Promise<T> | (() => T | Promise<T>), then: (v: T) => U, catch_?: () => never): U | Promise<U> {
    const v = typeof arg == 'function' ? (arg as () => T | Promise<T>)() : arg;

    if(v instanceof Promise)
        return v.then(then, catch_);
    return then(v);
}

function randomDigit(base: number) {
    base = base || 10;
    return Math.floor(Math.random() * base).toString(base);
}

export function yRandomInt(max: number) {
    return Math.floor(Math.random() * max);
}

export function yRandomSelect<T>(array: T[]) {
    return array[yRandomInt(array.length)];
}

export function yRandomDigits(length: number, base: number = 10) {
    let ret = '';
    for(let i = 0; i < length; i++)
        ret += randomDigit(base);
    return ret;
}

export function yNewUniqueId(prefix: string, length: number = 15, base: number = 36) {
    let dst = new Date().getTime().toString(base);
    while(dst.length < length)
        dst = randomDigit(base) + dst;
    return prefix + dst;
}

/**
 * @returns  A tuple containing the merged array and the positions of the elements from the second array in the merged array.
 */
export function yMergeSorted<T>(lhs: T[], rhs: T[], spec: ((lhs: T, rhs: T) => number) | YSortSpec<T>): [T[], number[]] {
    if(typeof spec == 'function')
        spec = [spec, 1];

    const o = spec[1];
    const fn = spec[0];
    const ret: T[] = [];
    const pos: number[] = [];

    let i = 0, j = 0;
    while(i < lhs.length && j < rhs.length) {
        if(fn(lhs[i], rhs[j], o) < 0) {
            ret.push(lhs[i++]);
        } else {
            pos.push(ret.length);
            ret.push(rhs[j++]);
        }
    }

    while(i < lhs.length) {
        ret.push(lhs[i++]);
    }

    while(j < rhs.length) {
        pos.push(ret.length);
        ret.push(rhs[j++]);
    }

    return [ret, pos];
}

export function yShuffleInline<T>(array: T[]) {
    for(let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
}

export function ySortInline<T>(array: T[], spec: YSortSpec<T>): T[] {
    const o = spec[1];
    const fn = spec[0];
    return array.sort((a, b) => fn(a, b, o));
}

export function ySortWithMapping<T>(array: T[], spec: ((lhs: T, rhs: T) => number) | YSortSpec<T>): [number[], T[]] {
    const positions = array.map((_value, index) => index);
    if(typeof spec == 'function') {
        positions.sort((a, b) => spec(array[a], array[b]));
    } else {
        const o = spec[1];
        const fn = spec[0];
        positions.sort((a, b) => fn(array[a], array[b], o));
    }
    return [positions, positions.map(p => array[p])];
}

///////////////////////
///// APPLICATION /////
///////////////////////
export const y_ready = new Promise<void>(resolve => {
    document.addEventListener('DOMContentLoaded', () => {
        resolve();
    });
});

export function yLoadAsyncScript(url: string, callback?: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;

        if(callback) {
            (window as any)[callback] = () => { resolve() };
        } else {
            script.onload = () => { resolve() };
        }

        script.onerror = () => { reject() }

        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode!.insertBefore(script, firstScriptTag);
    });
}

export function yAssert(condition: boolean, message?: string): asserts condition {
    if(!condition)
        throw new Error(message || "Assertion failed");
}

export function yDefer<T = void>() {
    const deferred: YDeferred<T> = {} as YDeferred<T>;

    deferred.promise = new Promise<T>((resolve, reject) => {
        deferred.resolve = resolve;
        deferred.reject = reject;
    });

    return deferred;
}

export function yMaybeDeferred<T>(v: T | Promise<T>, onfulfilled: (value: T) => unknown, onrejected?: (reason: any) => unknown) {
    if(v instanceof Promise) {
        v.then(onfulfilled, onrejected);
        return false;
    } else {
        onfulfilled(v);
        return true;
    }
}

export function yMaybeDeferredWithRejectValue<T>(v: T | Promise<T>, onfulfilled: (value: T) => unknown, reject_value: T) {
    if(v instanceof Promise) {
        v.then(onfulfilled, () => onfulfilled(reject_value));
        return false;
    } else {
        onfulfilled(v);
        return true;
    }
}

export function yWait(timeout: number) {
    return new Promise<void>(resolve => { setTimeout(resolve, timeout * 1000) });
}

export function yFillN<T>(n: number, v: (() => T) | T): T[] {
    const ret: T[] = [];
    if(typeof v === 'function') {
        for(let i = 0; i < n; i++) {
            ret.push((v as () => T)());
        }
    } else {
        for(let i = 0; i < n; i++) {
            ret.push(v);
        }
    }
    return ret;
}

/////////////////////////
///// EVENT HELPERS /////
/////////////////////////
export function yPropagationStopper(event: Event) {
    event.stopPropagation();
    event.preventDefault();
}

export function yDefaultPreventer(event: Event) {
    event.preventDefault();
}

export function yHtmlToText(html: string) {
    const elem = document.createElement('div');
    elem.innerHTML = html;
    return elem.textContent || '';
}

export function yTextToHtml(text: string) {
    const elem = document.createElement('div');
    elem.textContent = text;
    return elem.innerHTML;
}

export function yMakeMailtoLink(email?: string, subject?: string, body?: string) {
    let link = 'mailto:';
    if(email)
        link += encodeURIComponent(email);
    if(subject)
        link += '?subject=' + encodeURIComponent(subject);
    if(body)
        link += (subject ? '&body=' : '?body=') + encodeURIComponent(body);
    return link;
}

export function yMakeSMSLink(phone?: string, body?: string) {
    let link = 'sms:';
    if(phone)
        link += encodeURIComponent(phone);
    if(body)
        link += '?body=' + encodeURIComponent(body);
    return link;
}
